import {
  ArrowLeft,
  Building2,
  Check,
  ChevronsUpDown,
  LogOut,
  Settings,
  UserCircle,
} from "lucide-react";
import { useState } from "react";

import { PreferencesDialog } from "./preferences/PreferencesDialog";
import { usePreferencesDialog } from "./preferences/PreferencesDialogProvider";
import { InitialsAvatar } from "@/components/ui/initials-avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLogout } from "@/hooks/useLogout";
import { useSessionTenants, useSwitchTenant } from "@/hooks/useSessionTenants";
import { useAuthSession } from "@/lib/session-context";
import { cn } from "@/lib/utils";

/**
 * Bottom-of-sidebar user widget. Click pops a popover upward with two
 * views that swap inside the same surface:
 *
 *   - **menu** — identity header, Profile (stub), Preferences, Switch
 *     tenant (super admins), Sign out.
 *   - **tenants** — the tenants a super admin belongs to; picking one moves
 *     the session there (AB#8687).
 *
 * Preferences is too big for the popover: picking it closes the popover and
 * opens `PreferencesDialog`, the user's own server-side preferences — the
 * shell's (`PreferencesDialogProvider`), which outlives the page remount a
 * unit change causes; a dialog of this menu's own only outside the shell.
 *
 * Single Popover instead of nesting a Popover inside a DropdownMenu —
 * two portalled surfaces fight each other's outside-click detection and
 * focus traps, which caused the prefs panel to flash and immediately
 * close. One surface, internal `view` state, smooth swap.
 *
 * Compact mode collapses the trigger to just the avatar disc; the
 * popover content is identical to expanded mode.
 */
type View = "menu" | "tenants";

export function UserMenu({ compact }: { compact: boolean }) {
  const session = useAuthSession();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("menu");
  const shellPreferences = usePreferencesDialog();
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Reset to the menu view every time the popover closes so the next
  // open starts at the top level — never strands the user in a sub-view.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setView("menu");
  };

  const triggerInner = compact ? (
    <>
      <InitialsAvatar
        name={session.display_name}
        initials={session.initials}
        size={28}
      />
      <span className="absolute bottom-1 right-1 h-2 w-2 rounded-full bg-success ring-2 ring-background-deep" />
    </>
  ) : (
    <>
      <div className="relative">
        <InitialsAvatar
          name={session.display_name}
          initials={session.initials}
          size={28}
        />
        {/* Dot ring punches against the sidebar fill (`background-deep`)
            on rest, swaps to the hover/active fill (`row-hover`) when the
            row is hovered or the menu is open. */}
        <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-success ring-2 ring-background-deep transition-colors group-hover:ring-row-hover group-data-[state=open]:ring-row-hover" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-sm font-semibold text-foreground">
          {session.display_name}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {session.role_label}
        </span>
      </div>
      <ChevronsUpDown
        size={14}
        strokeWidth={1.75}
        className="shrink-0 text-text-dim transition-transform group-hover:text-foreground group-data-[state=open]:rotate-180"
      />
    </>
  );

  const triggerClass = compact
    ? "relative flex h-10 w-10 items-center justify-center rounded-md transition-colors hover:bg-row-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-row-hover"
    : cn(
        // Flush row — the surrounding footer (with its top-border separator)
        // already frames the widget, so a second card with its own border +
        // surface fill was visually redundant. Sits as a hoverable row in the
        // section, lifts to `row-hover` on hover / open.
        "group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left",
        "transition-colors hover:bg-row-hover",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "data-[state=open]:bg-row-hover",
      );

  const triggerButton = (
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={
          compact
            ? `Account menu — signed in as ${session.display_name}`
            : "Account menu"
        }
        className={triggerClass}
      >
        {triggerInner}
      </button>
    </PopoverTrigger>
  );

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        {compact ? (
          <Tooltip>
            <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
            <TooltipContent side="right">
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold">
                  {session.display_name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {session.role_label}
                </span>
              </div>
            </TooltipContent>
          </Tooltip>
        ) : (
          triggerButton
        )}

        <PopoverContent
          side="top"
          align="start"
          sideOffset={8}
          className="w-[260px] p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {view === "menu" && (
            <MenuView
              onPreferences={() => {
                setOpen(false);
                if (shellPreferences) shellPreferences.open();
                else setPreferencesOpen(true);
              }}
              onSwitchTenant={() => setView("tenants")}
              onClose={() => setOpen(false)}
            />
          )}
          {view === "tenants" && <TenantsView onBack={() => setView("menu")} />}
        </PopoverContent>
      </Popover>
      {shellPreferences ? null : (
        <PreferencesDialog open={preferencesOpen} onOpenChange={setPreferencesOpen} />
      )}
    </>
  );
}

/** Top-level account menu — identity header, links, sign out. Rendered
 *  inside the shared popover surface; selecting Preferences closes it and
 *  opens the Preferences dialog. */
function MenuView({
  onPreferences,
  onSwitchTenant,
  onClose,
}: {
  onPreferences: () => void;
  onSwitchTenant: () => void;
  onClose: () => void;
}) {
  const session = useAuthSession();
  const logout = useLogout();

  return (
    <div className="flex flex-col p-1">
      <div className="flex items-center gap-2.5 px-2 py-2">
        <InitialsAvatar
          name={session.display_name}
          initials={session.initials}
          size={32}
        />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate text-sm font-semibold text-foreground">
            {session.display_name}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {session.role_label}
          </span>
        </div>
      </div>

      <Separator />

      <SectionLabel>Account</SectionLabel>
      <MenuRow icon={UserCircle} label="Profile" disabled />
      <MenuRow
        icon={Settings}
        label="Preferences"
        onSelect={onPreferences}
      />
      {session.is_super_admin && (
        <MenuRow
          icon={Building2}
          label="Switch tenant"
          onSelect={onSwitchTenant}
          showChevron
        />
      )}

      <Separator />

      <MenuRow
        icon={LogOut}
        label={logout.isPending ? "Signing out…" : "Sign out"}
        destructive
        disabled={logout.isPending}
        onSelect={() => {
          logout.mutate(undefined, {
            onSettled: () => onClose(),
          });
        }}
      />
    </div>
  );
}

/** Selectable row inside `MenuView` — replaces what `DropdownMenuItem`
 *  was doing before. Same hover treatment, same destructive variant,
 *  same disabled state. Optional chevron hints "this opens another
 *  view" (used by Preferences). */
function MenuRow({
  icon: Icon,
  label,
  onSelect,
  destructive,
  disabled,
  showChevron,
}: {
  icon: typeof UserCircle;
  label: string;
  onSelect?: () => void;
  destructive?: boolean;
  disabled?: boolean;
  showChevron?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect?.()}
      disabled={disabled}
      className={cn(
        "flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
        "focus-visible:bg-row-hover focus-visible:outline-none",
        destructive
          ? "text-destructive hover:bg-destructive-dim disabled:opacity-50"
          : "text-foreground hover:bg-row-hover disabled:cursor-not-allowed disabled:opacity-50",
      )}
    >
      <Icon size={14} strokeWidth={1.75} />
      <span className="flex-1">{label}</span>
      {showChevron && (
        <ChevronsUpDown
          size={12}
          strokeWidth={1.75}
          className="rotate-90 text-text-dim"
        />
      )}
    </button>
  );
}

/** The tenants a super admin belongs to. Picking another one moves the
 *  session there and reloads the app in it; the current tenant is marked
 *  and not selectable. */
function TenantsView({ onBack }: { onBack: () => void }) {
  const tenants = useSessionTenants(true);
  const switchTenant = useSwitchTenant();

  return (
    <div className="flex flex-col p-1">
      <div className="flex items-center justify-between px-2 py-1.5">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={12} strokeWidth={2} />
          Back
        </button>
        <span className="text-sm font-semibold text-foreground">
          Switch tenant
        </span>
      </div>

      <Separator />

      {tenants.isLoading && (
        <div className="flex flex-col gap-1.5 px-2 py-1.5">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      )}
      {tenants.isError && (
        <p className="px-2 py-1.5 text-sm text-destructive">
          Could not load your tenants.
        </p>
      )}
      {(tenants.data ?? []).map((t) => (
        <button
          key={t.tenant_id}
          type="button"
          disabled={t.current || switchTenant.isPending}
          onClick={() => switchTenant.mutate(t.tenant_id)}
          className={cn(
            "flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
            "focus-visible:bg-row-hover focus-visible:outline-none",
            "text-foreground hover:bg-row-hover disabled:cursor-not-allowed",
          )}
        >
          <Building2 size={14} strokeWidth={1.75} />
          <span className="flex-1 truncate">{t.tenant_name}</span>
          {t.current && (
            <Check size={14} strokeWidth={2} className="text-primary" />
          )}
        </button>
      ))}
      {switchTenant.isError && (
        <p className="px-2 py-1.5 text-sm text-destructive">
          Could not switch to that tenant.
        </p>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 py-1.5 text-xs font-bold uppercase tracking-eyebrow text-text-dim">
      {children}
    </div>
  );
}

function Separator() {
  return <div className="-mx-1 my-1 h-px bg-border" />;
}
